<script setup lang="ts">
import { computed, type Component } from 'vue'

import { Icon } from '@/shared/ui/Icon'

/**
 * The tinted square a screen opens with, holding one glyph.
 *
 * It says what kind of screen this is before the heading is read — a letter, a lock — for a
 * page that stands on its own rather than sitting in a list.
 *
 * The tint is the theme's own (`--el-color-<tone>-light-9` behind `--el-color-<tone>`), so
 * pick a `tone` and never pass a colour: a hex here would not follow the palette.
 */
const props = withDefaults(
  defineProps<{
    icon: Component
    tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
    /** The square's side in px; the glyph is drawn at half of it. */
    size?: number
  }>(),
  { tone: 'primary', size: 56 }
)

const box = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  backgroundColor: `var(--el-color-${props.tone}-light-9)`
}))
</script>

<template>
  <div class="icon-tile" :style="box">
    <Icon :size="Math.round(size / 2)" :color="`var(--el-color-${tone})`">
      <component :is="icon" />
    </Icon>
  </div>
</template>

<style lang="scss" scoped>
.icon-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  border-radius: 14px;
}
</style>
